import {
  AnchorTag,
  FlexComp,
  FormattedText,
  H2Tag,
  H4Tag,
  ParaTag,
  Shimmer,
} from "@tm/static";
import ArrowRight from "@tm/static/icons/ArrowRight";
import { useBreakpointV2 } from "@tm/ui-widgets";
import React, { useEffect, useState } from "react";
import Chatbot from "../../components/Chatbot/Chatbot";

import { usePartnerContext } from "@tm/static/partnerContext";
import { TextNodeType } from "../FormattedText/FormattedText";
import TravelPlexBot from "../TravelPlexBot/TravelPlexBot";
import {
  DesktopLayoutBotttomSection,
  DesktopLayoutLeftSection,
  DesktopLayoutRightSection,
  DesktopLayoutStyleSection,
  DesktopLayoutStyleSectionWrap,
} from "./DesktopLayout.style";

interface LayoutProp {
  title?: any;
  leftComp?: React.ReactNode;
  rightComp?: React.ReactNode;
  bottomComp?: React.ReactNode;
  headingComp?: React.ReactNode;
  height?: string;
  alignCenter?: boolean;
  leftSectionMargin?: string | undefined;
  rightSectionGap?: string | undefined;
  rightSectionHeight?: string | undefined;
  leftSectionHeight?: boolean | undefined;
  landingMargin?: boolean | undefined;
  subTitle?: string | TextNodeType;
  shimmer?: boolean;
  landingPageProp?: boolean;
  goToMyOrders?: () => void;
  chatBotInfo?: {
    isEnabled: boolean;
    myraUrl: string;
    type: "MYRA" | "TRAVELPLEX" | undefined;
  };
  chatbotBottomMargin?: number;
  showMyOrder?: boolean;
  pageName?: string;
  leftPositionSticky?: boolean;
}

const DesktopLayout = ({
  title,
  chatBotInfo,
  leftComp,
  rightComp,
  bottomComp,
  headingComp,
  height,
  alignCenter,
  leftSectionMargin,
  rightSectionHeight,
  leftSectionHeight,
  landingMargin,
  subTitle,
  shimmer,
  landingPageProp,
  goToMyOrders,
  showMyOrder,
  pageName,
  chatbotBottomMargin,
  leftPositionSticky = true,
}: LayoutProp) => {
  const context = usePartnerContext();
  const device = useBreakpointV2();
  const [windowHeight, setWindowHeight] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    if (typeof window !== "undefined") {
      setWindowHeight(window.innerHeight);
      window.addEventListener("resize", handleResize);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, []);

  const getChatbot = () => {
    if (!chatBotInfo?.isEnabled) {
      return null;
    }

    if (chatBotInfo.type === "MYRA") {
      return (
        <Chatbot
          pageName={pageName}
          myraUrl={chatBotInfo.myraUrl}
          chatbotBottomMargin={chatbotBottomMargin}
        />
      );
    }

    return <TravelPlexBot view={pageName} />;
  };

  return (
    <DesktopLayoutStyleSectionWrap
      id={"pageId"}
      height={height}
      landingPageProp={landingPageProp}
    >
      <DesktopLayoutStyleSection
        landingMargin={landingMargin}
        alignCenter={alignCenter}
      >
        <DesktopLayoutLeftSection
          leftSectionHeight={leftSectionHeight}
          leftSectionMargin={leftSectionMargin}
          leftPositionSticky={leftPositionSticky}
        >
          <div>
            {shimmer ? (
              <FlexComp flexDirection="column" gap="4px">
                {subTitle && <Shimmer width="375px" height="38px" />}
                <Shimmer width="100%" height="56px" />
              </FlexComp>
            ) : (
              <>
                {subTitle &&
                  (device == "lg" ? (
                    <H4Tag
                      margin={device == "lg" ? "40px 0 0" : ""}
                      color="#CF8100"
                    >
                      <FormattedText value={subTitle} />
                    </H4Tag>
                  ) : (
                    <ParaTag fontSize="md" fontWeight="black" color="#CF8100">
                      <FormattedText value={subTitle} />
                    </ParaTag>
                  ))}
                {device == "lg" ? (
                  <H2Tag>
                    {title ? <FormattedText value={title} /> : null}
                  </H2Tag>
                ) : title?.html ? (
                  <FormattedText value={title} />
                ) : (
                  <H4Tag>
                    <FormattedText value={title} />
                  </H4Tag>
                )}
              </>
            )}
          </div>
          {leftComp}
        </DesktopLayoutLeftSection>
        <DesktopLayoutRightSection
          rightSectionHeight={rightSectionHeight}
          showMyOrder={showMyOrder}
        >
          {context && showMyOrder && landingPageProp && device == "lg" && (
            <FlexComp
              alignSelf="flex-end"
              isCursorPointer
              gap="4px"
              alignItems="center"
              margin="0 0 8px"
            >
              <AnchorTag
                fontWeight="bold"
                handleClick={goToMyOrders}
                color={`var(--tm-accent-color)`}
              >
                My Orders
              </AnchorTag>
              <ArrowRight width={16} height={16} />
            </FlexComp>
          )}
          {rightComp}
        </DesktopLayoutRightSection>
      </DesktopLayoutStyleSection>
      {bottomComp && (
        <DesktopLayoutBotttomSection>{bottomComp}</DesktopLayoutBotttomSection>
      )}
      {getChatbot()}
    </DesktopLayoutStyleSectionWrap>
  );
};

export default DesktopLayout;
